import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import ServiceApiService from "../services/service.service";
import type { ServiceConfirmationData } from "./models";

const getApiError = (err: unknown) => {
  if (typeof err !== "object" || err === null) {
    return null;
  }

  return err as {
    response?: {
      status?: number;
      data?: {
        errors?: {
          nonFieldErrors?: string[];
        };
      };
    };
  };
};

const ServiceCancel = () => {
  const { id } = useParams<{ id: string }>();
  const [serviceData, setServiceData] = useState<ServiceConfirmationData | null>(null);
  const [status, setStatus] = useState("new");
  const [error, setError] = useState<string | null>(null);
  const [isCanceling, setIsCanceling] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (!id) {
        setError("not found");
        return;
      }

      try {
        const response = await ServiceApiService.getServiceCancel(id);
        if (response.status === 200) {
          setServiceData(response.data.data);
          setStatus("ready");
        }
      } catch (err) {
        const apiError = getApiError(err);
        if (apiError?.response?.status === 400) {
          setError(
            apiError.response.data?.errors?.nonFieldErrors?.[0] || "not found"
          );
        }
      }
    };
    check();
  }, [id]);

  const confirmCancel = async () => {
    setIsCanceling(true);
    try {
      if (!id) {
        setError("not found");
        return;
      }

      const response = await ServiceApiService.cancelServiceByClient(id);
      if (response.status === 200) {
        setServiceData(response.data.data);
        setStatus("canceled");
      }
    } catch (err) {
      const apiError = getApiError(err);
      if (apiError?.response?.status === 400) {
        setError(apiError.response.data?.errors?.nonFieldErrors?.[0] || "error");
      }
    } finally {
      setIsCanceling(false);
    }
  };

  return (
    <div className="col-md-12">
      <div className="text-center p-4">
        <img
          src="https://lirp.cdn-website.com/a85f75f8/dms3rep/multi/opt/LayoutsDB67747D-C218-0AC4-C1B8-2184CDD25E0F.png_400-356w.png"
          alt="logo"
          width={200}
          className="mb-3"
        />
        {error ? (
          <div className="alert alert-danger">
            {error === "already used"
              ? "This cancellation link has already been used."
              : error === "wrong status"
              ? "Unable to cancel — appointment has wrong status."
              : "Appointment not found. Please contact us."}
          </div>
        ) : status === "new" ? (
          <>
            <p>Checking your appointment...</p>
            <Spinner animation="border" />
          </>
        ) : status === "ready" ? (
          <>
            <h4>Hello, {serviceData?.serviceClient}!</h4>
            <p>
              Are you sure you want to cancel your service appointment on{" "}
              <strong>{moment(serviceData?.serviceDate).format("LLL")}</strong>?
            </p>
            <Button variant="danger" onClick={confirmCancel} disabled={isCanceling}>
              {isCanceling ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Cancel Appointment"
              )}
            </Button>
          </>
        ) : (
          <>
            <h4>Your appointment has been canceled.</h4>
            <p>
              If you'd like to reschedule, please call{" "}
              <a href={`tel:${serviceData?.companyPhoneNumber}`}>{serviceData?.companyPhoneNumber}</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceCancel;
